import { useMemo, useState, Fragment } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { journeyApi } from "@/lib/journeyApi";
import { EmptyState } from "@/components/DashboardShell";
import { ConfirmedOverTime, AllocationDistribution, InsufficientData, C } from "@/components/DashboardCharts";
import { fmt } from "@/lib/format";

const fmt0 = (n) => fmt(Math.round(Number(n || 0))); // whole-dollar display for summary/position
const SLICE = ["#2F6B57", "#C9A86A", "#7FA491", "#164C3B", "#D9C08A", "#A89A88"];
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };
const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

const Panel = ({ title, sub, children, testid, right, className = "" }) => (
  <section className={`rounded-2xl border border-[#ECE6DC] bg-white p-5 sm:p-6 ${className}`} data-testid={testid}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3">
        <div>
          {title && <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">{title}</h3>}
          {sub && <p className="mt-1 text-sm text-[#8A7C6B]">{sub}</p>}
        </div>
        {right}
      </div>
    )}
    <div className={title || right ? "mt-4" : ""}>{children}</div>
  </section>
);

const Metric = ({ label, value, note, tid, big }) => (
  <div data-testid={tid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A89A88]">{label}</p>
    <p className={`mt-1 font-serif ${big ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"} text-[#171717] tabular-nums`}>{value}</p>
    {note && <p className="mt-0.5 text-xs text-[#8A7C6B]">{note}</p>}
  </div>
);

// ── Since your last Edit ──────────────────────────────────────────────────────
function SinceLastEdit({ history, goal, currentDebt, protection }) {
  if (!history?.length) return null;
  const h = history[history.length - 1];
  const amt = h.amount_confirmed || 0;
  let big, line, sub;
  if (h.category === "build") {
    big = `+${fmt0(amt)}`; line = `Added toward ${h.goal_name || "your goal"}`;
    if (goal && !goal.open_ended && goal.target > 0) {
      const after = goal.percent_funded;
      const before = Math.max(0, Math.round(((goal.funded - amt) / goal.target) * 100));
      sub = `Your goal moved from ${before}% to ${after}% funded.`;
    }
  } else if (h.category === "debt") {
    big = `−${fmt0(amt)}`; line = "Off your consumer debt";
    sub = `Your debt moved from ${fmt0(currentDebt + amt)} to ${fmt0(currentDebt)}.`;
  } else {
    big = `+${fmt0(amt)}`; line = "Added to your protection";
    const now = (protection && protection.funded) || 0;
    sub = `Your protected runway moved from ${fmt0(now - amt)} to ${fmt0(now)}.`;
  }
  return (
    <div data-testid="progress-since-last">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Since your last Edit</p>
      <p className="mt-2 font-serif text-3xl text-[#164C3B] tabular-nums sm:text-4xl">{big}</p>
      <p className="mt-1 text-sm text-[#525252]">{line}{h.completed_goal ? " — goal complete" : h.completed_debt ? " — debt eliminated" : ""}.</p>
      {sub && <p className="mt-1 text-sm text-[#8A7C6B]">{sub}</p>}
    </div>
  );
}

// ── Financial Journeys (tabbed: Debt / Goals / Protection) ───────────────────
function JourneyDebt({ data }) {
  const timeline = (data.timeline || []).map((p, i) => ({ ...p, x: p.point === "start" ? "Start" : fmtShort(p.date) || `#${i}` }));
  return (
    <div data-testid="progress-debt">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Metric label="Starting debt" value={fmt0(data.starting_debt || 0)} tid="progress-starting" />
        <Metric label="Current debt" value={fmt0(data.current_debt || 0)} tid="progress-current" />
        <Metric label="Debt eliminated" value={fmt0(data.total_attacked || 0)} tid="progress-attacked" />
        <Metric label="Debts paid off" value={data.debts_eliminated || 0} tid="progress-eliminated" />
      </div>
      {timeline.length >= 2 ? (
        <div className="mt-5 h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeline} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
              <defs><linearGradient id="dj" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.emerald} stopOpacity={0.26} /><stop offset="100%" stopColor={C.emerald} stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
              <XAxis dataKey="x" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `$${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k`} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E5E0D9", fontSize: 13 }} formatter={(v) => [fmt0(v), "Debt remaining"]} labelFormatter={(l) => (l === "Start" ? "Before first Edit" : l)} />
              <Area type="monotone" dataKey="total_debt" stroke={C.emerald} strokeWidth={2.5} fill="url(#dj)" dot={{ r: 2.5, fill: C.emerald }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (data.starting_debt > 0 ? (
        <div className="mt-5" data-testid="progress-debt-compare">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <p className="text-xs text-[#8A7C6B]">Starting</p>
              <div className="mt-1 h-3 w-full rounded-full bg-[#2F6B57]" />
              <p className="mt-1 text-sm font-medium text-[#171717] tabular-nums">{fmt0(data.starting_debt)}</p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-[#8A7C6B]">Current</p>
              <div className="mt-1 h-3 rounded-full bg-[#DDE7DF]" style={{ width: `${data.starting_debt ? Math.max(2, (data.current_debt / data.starting_debt) * 100) : 0}%` }} />
              <p className="mt-1 text-sm font-medium text-[#171717] tabular-nums">{fmt0(data.current_debt)}</p>
            </div>
          </div>
        </div>
      ) : <div className="mt-5"><InsufficientData testid="progress-debt-journey-empty">No consumer debt on record.</InsufficientData></div>)}
    </div>
  );
}

function JourneyGoals({ goal, history }) {
  if (!goal) return <InsufficientData testid="progress-goal-empty">No active goal yet — choose one from My Goal.</InsufficientData>;
  const buildHistory = (history || []).filter((h) => h.category === "build" && (goal.id ? (h.goal_id === goal.id || !h.goal_id) : true));
  const pct = goal.open_ended ? null : goal.percent_funded;
  return (
    <div data-testid="progress-goal">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Metric label="Funded" value={fmt0(goal.funded || 0)} tid="goal-contributed" />
        <Metric label="Target" value={goal.open_ended ? "Open" : fmt0(goal.target || 0)} />
        <Metric label="Build Edits" value={goal.completed_count ?? buildHistory.length} tid="goal-count" />
        <Metric label="Remaining" value={goal.open_ended ? "—" : fmt0(goal.remaining || 0)} tid="goal-remaining" />
      </div>
      {pct != null && (
        <div className="mt-4">
          <div className="h-3 w-full overflow-hidden rounded-full bg-[#EDE7DC]"><div className="h-full rounded-full bg-[#2F6B57]" style={{ width: `${Math.min(100, pct)}%` }} /></div>
          <p className="mt-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">{pct}% funded</p>
        </div>
      )}
      <div className="mt-5 border-t border-[#F0EBE2] pt-4">
        <ConfirmedOverTime history={buildHistory} testid="chart-goal-journey" color={C.green} label={`${goal.name} to date`} />
      </div>
    </div>
  );
}

function JourneyProtection({ protection }) {
  if (!protection) return <InsufficientData testid="progress-protection-empty">Protection analytics appear once protection applies to your plan.</InsufficientData>;
  const funded = protection.funded || 0, target = protection.target || 0;
  const added = protection.added_from_sprints || 0;
  const existing = funded - added;
  const above = Math.round(funded - target);
  const below = protection.state === "below";
  const statusLabel = below ? `${fmt0(protection.remaining || 0)} to target` : protection.state === "above" ? "Target exceeded" : "Target met";
  const barPct = target > 0 ? Math.min(100, Math.round((funded / target) * 100)) : 0;
  return (
    <div data-testid="progress-protection">
      <p className="text-sm text-[#8A7C6B]">Current runway compared with your recommended target.</p>
      <div className="mt-3">
        <div className="flex items-end justify-between text-sm">
          <span className="font-serif text-xl text-[#164C3B] tabular-nums">{fmt0(funded)}</span>
          <span className="text-[#8A7C6B] tabular-nums">Target {fmt0(target)}</span>
        </div>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-[#EADFC7]"><div className="h-full rounded-full bg-[#C9A86A]" style={{ width: `${barPct}%` }} /></div>
        <p className="mt-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">{statusLabel}</p>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-[#F0EBE2] pt-4 sm:grid-cols-4">
        <Metric label="Existing protected runway" value={fmt0(existing)} tid="protection-existing" />
        <Metric label="Recommended target" value={fmt0(target)} tid="protection-target" />
        <Metric label="Added through confirmed Edits" value={fmt0(added)} tid="protection-added" />
        {below ? <Metric label="Remaining to target" value={fmt0(protection.remaining || 0)} tid="protection-remaining" />
               : <Metric label="Above target" value={fmt0(Math.max(0, above))} tid="protection-above" />}
      </div>
    </div>
  );
}

function FinancialJourneys({ data, goal, history, protection }) {
  const hasDebt = (data.starting_debt || 0) > 0 || (data.composition || []).length > 0;
  const tabs = [hasDebt && ["debt", "Debt"], goal && ["goals", "Goals"], protection && ["protection", "Protection"]].filter(Boolean);
  const [tab, setTab] = useState(tabs[0] ? tabs[0][0] : "goals");
  if (!tabs.length) return null;
  return (
    <Panel title="Your financial journeys" testid="progress-journeys"
      right={
        <div className="flex gap-1.5 rounded-full border border-[#ECE6DC] bg-[#FCFBF8] p-1" role="tablist">
          {tabs.map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={tab === v} data-testid={`journey-tab-${v}`} onClick={() => setTab(v)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition sm:text-sm ${tab === v ? "bg-[#164C3B] text-white" : "text-[#70584A] hover:text-[#164C3B]"}`}>{l}</button>
          ))}
        </div>
      }>
      {tab === "debt" && <JourneyDebt data={data} />}
      {tab === "goals" && <JourneyGoals goal={goal} history={history} />}
      {tab === "protection" && <JourneyProtection protection={protection} />}
    </Panel>
  );
}

// ── Confirmed History (ledger + filter totals + row detail) ──────────────────
const CAT = { debt: ["Debt", "#2F6B57"], protection: ["Protection", "#C9A86A"], build: ["Goal", "#164C3B"] };
const destOf = (h) => h.category === "protection" ? "Protected Runway" : h.category === "build" ? (h.goal_name || h.target_name) : h.target_name;
const statusOf = () => "Confirmed";
// RESULT = what the confirmed allocation accomplished (kept separate from STATUS).
const resultOf = (h) => {
  if (h.category === "build") return h.completed_goal ? "Goal completed" : "Goal in progress";
  if (h.category === "debt") return h.completed_debt ? "Debt eliminated" : "Debt reduced";
  if (h.category === "protection") return "Protection added";
  return "Confirmed";
};

function RowDetail({ h }) {
  const [showForm, setShowForm] = useState(false);
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("Confirmed wrong amount");
  const [busy, setBusy] = useState(false);
  const canCorrect = h.category === "build";
  const effective = h.amount_confirmed || 0;
  const original = h.corrected ? h.original_amount : effective;
  const bits = [];
  if (h.landed_money) bits.push(`Money landed ${fmt0(h.landed_money)}`);
  if (h.category === "debt" && (h.debt_allocations || []).length) h.debt_allocations.forEach((p) => bits.push(`${p.name}: ${fmt0(p.applied_amount)}${p.eliminated ? " (paid off)" : ""}`));
  if (h.category === "protection") bits.push(`To protected runway: ${fmt0(h.amount_confirmed)}`);
  if (h.category === "build") bits.push(`To ${h.goal_name || "goal"}: ${fmt0(effective)}`);
  const submit = async () => {
    const dollars = Math.round(Number(amt) * 100) / 100;
    if (!dollars || dollars <= 0 || dollars > effective) { toast.error(`Enter an amount between $1 and ${fmt0(effective)}.`); return; }
    setBusy(true);
    try {
      await journeyApi.recordCorrection(h.id, -dollars, reason);
      try { await journeyApi.trackEvent?.("edit_corrected", {}); } catch {}
      toast.success("Correction recorded. Your original confirmation remains in The Receipts.");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record correction."); setBusy(false); }
  };
  return (
    <div className="bg-[#FCFBF8] px-4 py-3 text-xs text-[#70584A]" data-testid="history-row-detail">
      <p><span className="font-semibold uppercase tracking-[0.1em] text-[#A89A88]">Result</span> · <span className="font-medium text-[#164C3B]">{resultOf(h)}</span></p>
      {h.corrected && (
        <div className="mt-2 rounded-lg border border-[#ECE6DC] bg-white p-2.5" data-testid="history-correction-detail">
          <div className="flex justify-between"><span>Original confirmation</span><span className="tabular-nums text-[#171717]">{fmt0(original)}</span></div>
          <div className="flex justify-between"><span>Correction</span><span className="tabular-nums text-[#B0603A]">{fmt0(h.correction_total)}</span></div>
          <div className="flex justify-between font-medium"><span>Effective confirmed amount</span><span className="tabular-nums text-[#164C3B]">{fmt0(effective)}</span></div>
          {(h.corrections || []).map((c, i) => <p key={i} className="mt-1 text-[#A89A88]">Reason: {c.reason}</p>)}
        </div>
      )}
      <p className="mt-1">{bits.join(" · ") || "Confirmed move."}</p>
      {canCorrect && !showForm && (
        <button type="button" data-testid="report-correction-btn" onClick={() => setShowForm(true)} className="mt-2 font-medium text-[#2F6B57] hover:underline">Report a correction</button>
      )}
      {canCorrect && showForm && (
        <div className="mt-2 rounded-lg border border-[#C9A86A]/50 bg-white p-3" data-testid="correction-form">
          <p className="font-medium text-[#171717]">Report a correction</p>
          <p className="mt-0.5 text-[#8A7C6B]">Your original confirmation ({fmt0(original)} toward {h.goal_name || "your goal"}) will remain in The Receipts. This correction will update the effective totals.</p>
          <label className="mt-2 block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Amount to remove</label>
          <input data-testid="correction-amount" value={amt} inputMode="decimal" onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0"
            className="mt-1 w-full rounded-lg border border-[#DDE7DF] px-3 py-2 text-sm text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
          <select data-testid="correction-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-2 w-full rounded-lg border border-[#DDE7DF] bg-white px-3 py-2 text-sm text-[#171717] focus:border-[#2F6B57] focus:outline-none">
            <option>Confirmed wrong amount</option>
            <option>Did not complete this action</option>
            <option>Duplicate confirmation</option>
            <option>Other</option>
          </select>
          <div className="mt-3 flex gap-2">
            <button type="button" data-testid="correction-submit" disabled={busy} onClick={submit}
              className="min-h-10 rounded-lg bg-[#2F6B57] px-4 text-sm font-semibold text-white hover:bg-[#255643] disabled:opacity-60">RECORD CORRECTION</button>
            <button type="button" data-testid="correction-cancel" disabled={busy} onClick={() => setShowForm(false)}
              className="min-h-10 rounded-lg px-4 text-sm font-medium text-[#70584A] hover:bg-[#F5F2EC]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmedHistory({ history }) {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const tabs = [["all", "All"], ["debt", "Debt"], ["protection", "Protection"], ["build", "Goals"]];
  const items = useMemo(() => {
    const base = filter === "all" ? history : (history || []).filter((h) => h.category === filter);
    return [...(base || [])].sort((a, b) => new Date(b.date) - new Date(a.date)); // copy, newest-first
  }, [history, filter]);
  const filteredTotal = items.reduce((s, h) => s + (h.amount_confirmed || 0), 0);
  if (!history?.length) return <p className="text-sm text-[#A89A88]" data-testid="canonical-history-empty">No confirmed Edits yet. Your first move will show here.</p>;
  return (
    <div data-testid="canonical-history">
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map(([v, l]) => (
          <button key={v} type="button" data-testid={`history-filter-${v}`} onClick={() => { setFilter(v); setOpen(null); }}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${filter === v ? "bg-[#164C3B] text-white" : "border border-[#ECE6DC] bg-white text-[#70584A] hover:border-[#C9A86A]"}`}>{l}</button>
        ))}
      </div>
      <p className="mb-3 text-xs text-[#8A7C6B]" data-testid="history-filter-total">Showing {items.length} confirmed {items.length === 1 ? "move" : "moves"} · {fmt0(filteredTotal)} total</p>
      {items.length === 0 ? <p className="text-sm text-[#A89A88]">Nothing in this category yet.</p> : (<>
        {/* Desktop ledger table */}
        <div className="hidden overflow-hidden rounded-xl border border-[#ECE6DC] sm:block">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#ECE6DC] bg-[#FCFBF8] text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">
              <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Destination</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Status</th><th className="w-8 px-2 py-2.5" />
            </tr></thead>
            <tbody>
              {items.map((h) => { const [label, color] = CAT[h.category] || ["Edit", "#70584A"]; const isOpen = open === h.id; return (
                <Fragment key={h.id}>
                  <tr className="cursor-pointer border-b border-[#F0EBE2] last:border-0 hover:bg-[#FCFBF8]" data-testid="canonical-history-item" onClick={() => setOpen(isOpen ? null : h.id)}>
                    <td className="whitespace-nowrap px-4 py-2.5 text-[#70584A]">{fmtDate(h.date)}</td>
                    <td className="px-4 py-2.5 font-medium text-[#171717]">{destOf(h)}</td>
                    <td className="px-4 py-2.5"><span className="rounded-full px-2.5 py-0.5 text-[0.7rem] font-semibold text-white" style={{ background: color }}>{label}</span></td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold text-[#164C3B] tabular-nums">{fmt0(h.amount_confirmed)}{h.corrected && <span className="ml-1.5 rounded-full bg-[#F3E9D7] px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-[#8A6D2F]" data-testid="history-corrected-badge">Corrected</span>}</td>
                    <td className="px-4 py-2.5 text-[#70584A]">{statusOf(h)}</td>
                    <td className="px-2 py-2.5 text-[#C9A86A]"><ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></td>
                  </tr>
                  {isOpen && <tr><td colSpan={6} className="p-0"><RowDetail h={h} /></td></tr>}
                </Fragment>
              ); })}
            </tbody>
          </table>
        </div>
        {/* Mobile rows */}
        <div className="divide-y divide-[#F0EBE2] sm:hidden">
          {items.map((h) => { const [label, color] = CAT[h.category] || ["Edit", "#70584A"]; const isOpen = open === h.id; return (
            <div key={h.id} data-testid="canonical-history-item">
              <button type="button" className="flex w-full items-center justify-between gap-3 py-3 text-left" onClick={() => setOpen(isOpen ? null : h.id)}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-[#171717]">{fmt0(h.amount_confirmed)} · {destOf(h)}{h.corrected && <span className="ml-1.5 rounded-full bg-[#F3E9D7] px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-[#8A6D2F]">Corrected</span>}</p>
                  <p className="text-xs text-[#A89A88]">{fmtDate(h.date)} · {statusOf(h)}</p>
                </div>
                <span className="shrink-0 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold text-white" style={{ background: color }}>{label}</span>
              </button>
              {isOpen && <div className="pb-3"><RowDetail h={h} /></div>}
            </div>
          ); })}
        </div>
      </>)}
    </div>
  );
}

function Composition({ composition }) {
  if (!composition?.length) return null;
  const total = composition.reduce((s, c) => s + c.balance, 0);
  return (
    <Panel title="Current debt composition" testid="progress-debt-composition">
      <div className="flex flex-col items-center gap-4 sm:flex-row" data-testid="progress-composition">
        <div className="h-36 w-36 shrink-0" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={composition} dataKey="balance" nameKey="name" innerRadius={40} outerRadius={66} paddingAngle={2} stroke="none">
                {composition.map((c, i) => <Cell key={c.debt_id} fill={SLICE[i % SLICE.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [fmt0(v), n]} contentStyle={{ borderRadius: 10, border: "1px solid #E5E0D9", fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="w-full space-y-2" data-testid="progress-composition-legend">
          {composition.map((c, i) => (
            <li key={c.debt_id} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-[#525252]"><span className="h-3 w-3 rounded-sm" style={{ background: SLICE[i % SLICE.length] }} />{c.name}</span>
              <span className="font-medium text-[#171717] tabular-nums">{fmt0(c.balance)} <span className="text-xs text-[#A89A88]">({Math.round((c.balance / total) * 100)}%)</span></span>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

export function ProgressCenter({ data }) {
  const goals = data.goals || { active: data.goal, completed: [] };
  const goal = goals.active;
  const history = data.sprint_history || [];
  const prot = data.protection;
  const lt = data.lifetime || {};

  // Canonical reconciling totals: TOTAL ALLOCATED = debt + goals + protection.
  const debtElim = lt.debt_attacked != null ? lt.debt_attacked : (data.total_attacked || 0);
  const toGoals = lt.goals_built != null ? lt.goals_built : history.filter((h) => h.category === "build").reduce((s, h) => s + (h.amount_confirmed || 0), 0);
  const toProtection = lt.protected != null ? lt.protected : (prot && prot.added_from_sprints) || 0;
  const totalAllocated = lt.money_put_to_work != null ? lt.money_put_to_work : (debtElim + toGoals + toProtection);
  const editCount = lt.confirmed_sprints != null ? lt.confirmed_sprints : history.length;
  const debtsPaid = data.debts_eliminated || 0;

  const nothing = history.length === 0 && !goal && !(goals.completed || []).length && !(prot && prot.funded > 0);
  if (nothing) return <div className="mt-6"><EmptyState icon={TrendingUp} title="No confirmed Edits yet." body="Every confirmed Protection, debt and Build move shows up here — drawn from Edits you confirmed." testid="progress-empty" /></div>;

  // Editorial hero (dynamic, graceful when a category is zero).
  let heroH1, heroLine;
  if (debtElim > 0 && toGoals > 0) { heroH1 = `You eliminated ${fmt0(debtElim)} in consumer debt.`; heroLine = `And directed ${fmt0(toGoals)} toward your goals.`; }
  else if (debtElim > 0) { heroH1 = `You eliminated ${fmt0(debtElim)} in consumer debt.`; }
  else if (toGoals > 0) { heroH1 = `You directed ${fmt0(toGoals)} toward your goals.`; }
  else if (toProtection > 0) { heroH1 = `You added ${fmt0(toProtection)} to your protection.`; }
  else { heroH1 = "Your confirmed Edits, in one place."; }

  // Current stage (authoritative, display-only from backend; never auto-Freedom).
  const STAGE_LABEL = { eliminate: "ELIMINATE", protect: "PROTECT", build: "BUILD", freedom: "FREEDOM", build_unassigned: "WHAT'S NEXT" };
  const stage = STAGE_LABEL[data.current_stage] || ((data.current_debt || 0) > 0 ? "ELIMINATE" : (prot && prot.state === "below") ? "PROTECT" : goal ? "BUILD" : "WHAT'S NEXT");

  const StandDebt = () => (data.current_debt || 0) > 0
    ? <Metric label="Debt" value={fmt0(data.current_debt)} note="remaining" tid="stand-debt" />
    : <Metric label="Debt" value="$0" note="Debt free" tid="stand-debt" />;
  const StandGoal = () => goal
    ? <Metric label={goal.name || "Major goal"} value={goal.open_ended ? fmt0(goal.funded) : `${goal.percent_funded}%`} note={goal.open_ended ? "Open goal" : `${fmt0(goal.funded)} of ${fmt0(goal.target)} · ${fmt0(goal.remaining)} remaining`} tid="stand-goal" />
    : <Metric label="Major goal" value="—" note="None selected" tid="stand-goal" />;
  const StandProt = () => prot
    ? <Metric label="Protection" value={fmt0(prot.funded || 0)} note={prot.state === "below" ? `${fmt0(prot.remaining || 0)} to target` : prot.state === "above" ? "Target exceeded" : "Target met"} tid="stand-protection" />
    : null;

  return (
    <div className="space-y-5" data-testid="progress-center">
      {/* Editorial hero */}
      <header className="pt-1" data-testid="progress-summary">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#B8923F]">The Receipts</p>
        <h2 className="mt-2 max-w-2xl font-serif text-3xl leading-tight text-[#171717] sm:text-4xl">{heroH1}</h2>
        {heroLine && <p className="mt-1.5 font-serif text-2xl leading-snug text-[#2F6B57] sm:text-3xl">{heroLine}</p>}
        <p className="mt-3 text-sm text-[#8A7C6B]">Every number here comes from an Edit you confirmed.</p>
      </header>

      {/* ONE canonical summary (reconciles: total = debt + goals + protection) */}
      <div className="grid grid-cols-2 divide-x divide-y divide-[#ECE6DC] overflow-hidden rounded-2xl border border-[#ECE6DC] bg-white sm:grid-cols-4 sm:divide-y-0" data-testid="progress-kpis">
        <div className="p-4 sm:p-5"><Metric label="Total allocated" value={fmt0(totalAllocated)} note={`Across ${editCount} confirmed ${editCount === 1 ? "Edit" : "Edits"}`} tid="kpi-put-to-work" big /></div>
        <div className="p-4 sm:p-5"><Metric label="Debt eliminated" value={fmt0(debtElim)} note={`${debtsPaid} ${debtsPaid === 1 ? "debt" : "debts"} paid off`} tid="kpi-debt-eliminated" big /></div>
        <div className="p-4 sm:p-5"><Metric label="Allocated to goals" value={fmt0(toGoals)} note="Across all goals" tid="kpi-built-goals" big /></div>
        <div className="p-4 sm:p-5"><Metric label="Allocated to protection" value={fmt0(toProtection)} note="Through confirmed Edits" tid="kpi-protected" big /></div>
      </div>

      {/* Where you stand now + Since your last Edit */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Panel title="Where you stand now" sub={`Current stage: ${stage}`} testid="progress-stand" className="lg:col-span-7">
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
            <StandDebt /><StandGoal /><StandProt />
          </div>
        </Panel>
        <Panel testid="progress-since" className="lg:col-span-5">
          <SinceLastEdit history={history} goal={goal} currentDebt={data.current_debt || 0} protection={prot} />
        </Panel>
      </div>

      {/* Primary + allocation */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Panel title="Your progress over time" sub="Confirmed dollars, Edit by Edit." testid="progress-primary-chart" className="lg:col-span-8">
          <ConfirmedOverTime history={history} testid="chart-cumulative" label="Total allocated to date" bySequence startPoint={false} amountLabel="Allocated this Edit" />
        </Panel>
        <Panel title="Where your confirmed money went" sub="Protect. Eliminate. Build." testid="progress-allocation" className="lg:col-span-4">
          <AllocationDistribution history={history} protection={prot} testid="chart-distribution" />
        </Panel>
      </div>

      {/* Consolidated journeys */}
      <FinancialJourneys data={data} goal={goal} history={history} protection={prot} />
      <Composition composition={data.composition || []} />

      {/* Confirmed history */}
      <Panel title="Confirmed history" sub="Every move you confirmed, newest first." testid="section-history">
        <ConfirmedHistory history={history} />
      </Panel>
    </div>
  );
}

export function CanonicalHistory({ history }) { return <ConfirmedHistory history={history} />; }
